import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/apiClient";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(
        u.role === "agency_catnat" ? "/catnat-inbox"
          : ["agency_admin", "agency_operator"].includes(u.role) ? "/admin" : "/"
      );
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-bone">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-forest p-12 lg:flex">
        <img
          src="https://images.unsplash.com/photo-1758599668299-beebedfabf7b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Volontari del Terzo Settore"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="relative z-10">
          <Brand light />
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl font-semibold leading-tight text-white lg:text-5xl">
            Collaborazioni sane per l'Economia Solidale.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-white/75">
            Il portale dedicato alla rete di Broker, Subagenti, Produttori e Collaboratori.
            Tre clic per un preventivo, con la sicurezza della blindatura documentale IVASS.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-sm text-white/70">
          <ShieldCheck className="h-4 w-4" /> Accesso su mandato — nessuna auto-registrazione
        </div>
      </div>

      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="mb-8 lg:hidden">
            <Brand />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-forest">Accedi al portale</h2>
          <p className="mt-1 text-sm text-muted-foreground">Inserisci le credenziali fornite dall'Agenzia.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@esempio.it"
                className="h-12"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="h-12 w-full rounded-xl bg-terracotta text-base font-medium hover:bg-terracotta-hover"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entra"}
            </Button>
          </form>
          <p className="mt-6 text-xs text-muted-foreground">
            Password dimenticata? Contatta l'Agenzia: il reset è gestito dall'Admin.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
